import type { JumpTarget } from '@/components/MagicJump';
import { env } from '@/utils/env';
import { EPlatform } from '@/utils/enums/platform-enum';
import type {
  CollaborationDevice,
  CollaborationGalleryImageData,
  CollaborationResolvedProduct,
  CollaborationRuntimeData,
  CollaborationSeriesItemData,
  RawCollaborationBlocks,
} from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function identifier(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : text(value);
}

export function resolveCollaborationAssetUrl(
  value: unknown,
  assetBaseUrl = env.NEXT_PUBLIC_S3_URL,
): string {
  const path = text(value);
  if (!path || /^(https?:)?\/\//i.test(path) || path.startsWith('blob:')) return path;
  return `${assetBaseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

function positiveInteger(value: unknown): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
}

function sortIndex(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function displayDevices(value: unknown): CollaborationDevice[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (device): device is CollaborationDevice => device === 'pc' || device === 'wap',
  );
}

function isVisibleOnDevice(value: unknown, deviceType: EPlatform): boolean {
  const currentDevice: CollaborationDevice = deviceType === EPlatform.WEB ? 'pc' : 'wap';
  return displayDevices(value).includes(currentDevice);
}

function productSku(product: Record<string, unknown>): string {
  return text(product.shop_sku) || text(product.shopSku);
}

function normalizeProduct(value: unknown): CollaborationResolvedProduct | undefined {
  if (!isRecord(value)) return;
  const shopSku = productSku(value);
  if (!shopSku) return;
  return {
    ...value,
    shop_sku: shopSku,
    shopSku,
    name: text(value.name) || text(value.short_name) || shopSku,
  };
}

function normalizeSeriesItems(value: unknown): CollaborationSeriesItemData[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isRecord)
    .map((item, index) => ({ item, sort: sortIndex(item.sort, index), index }))
    .sort((left, right) => left.sort - right.sort || left.index - right.index)
    .slice(0, 4)
    .map(({ item, sort }) => {
      const productBySku = new Map<string, CollaborationResolvedProduct>();
      if (Array.isArray(item.products)) {
        item.products.forEach((value) => {
          const product = normalizeProduct(value);
          if (product) productBySku.set(product.shopSku, product);
        });
      }
      const products = Array.isArray(item.sku_ids)
        ? item.sku_ids
            .slice(0, 2)
            .map((sku) => productBySku.get(text(sku)))
            .filter((product): product is CollaborationResolvedProduct => Boolean(product))
        : [];
      return {
        sort,
        name: text(item.name),
        pcCardImage: text(item.pc_card_image),
        wapCardImage: text(item.wap_card_image),
        subTitle: text(item.sub_title),
        pcSceneImage: text(item.pc_scene_image),
        wapSceneImage: text(item.wap_scene_image),
        products,
      };
    });
}

export function collaborationSeriesProductIds(
  series: CollaborationRuntimeData['series'],
): number[] {
  const ids = series.items.flatMap((item) =>
    item.products
      .filter((product) => !Array.isArray(product.correlation))
      .map((product) => positiveInteger(product.id))
      .filter(Boolean),
  );
  return Array.from(new Set(ids));
}

export function mergeCollaborationSeriesProducts(
  series: CollaborationRuntimeData['series'],
  enrichedProducts: unknown,
): CollaborationRuntimeData['series'] {
  if (!Array.isArray(enrichedProducts)) return series;

  const enrichedBySku = new Map<string, Record<string, unknown>>();
  enrichedProducts.filter(isRecord).forEach((product) => {
    const sku = productSku(product);
    if (sku) enrichedBySku.set(sku, product);
  });

  return {
    ...series,
    items: series.items.map((item) => ({
      ...item,
      products: item.products.map((product) => {
        const enriched = enrichedBySku.get(product.shopSku);
        if (!enriched) return product;
        return {
          ...product,
          ...enriched,
          shop_sku: product.shop_sku,
          shopSku: product.shopSku,
          name: text(enriched.name) || product.name,
        };
      }),
    })),
  };
}

function normalizeGalleryImages(value: unknown): CollaborationGalleryImageData[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isRecord)
    .map((item, index) => ({ item, sort: sortIndex(item.sort, index), index }))
    .sort((left, right) => left.sort - right.sort || left.index - right.index)
    .slice(0, 10)
    .map(({ item, sort }) => {
      const product = normalizeProduct(item.product);
      return {
        sort,
        image: text(item.image),
        ...(product ? { product } : {}),
      };
    })
    .filter((item) => Boolean(item.image));
}

export function normalizeCollaborationRuntimeData(
  blocks: RawCollaborationBlocks | null | undefined,
  deviceType: EPlatform,
): CollaborationRuntimeData {
  const source = isRecord(blocks) ? blocks : {};
  const banner = isRecord(source.collaboration_banner) ? source.collaboration_banner : {};
  const series = isRecord(source.collaboration_series) ? source.collaboration_series : {};
  const media = isRecord(source.collaboration_media) ? source.collaboration_media : {};
  const video = isRecord(media.video) ? media.video : {};
  const gallery = isRecord(media.gallery) ? media.gallery : {};
  const productList = isRecord(source.collaboration_product_list)
    ? source.collaboration_product_list
    : {};
  const productRecommend = isRecord(source.product_recommend) ? source.product_recommend : null;

  return {
    banner: {
      moduleId: identifier(banner.module_id) || identifier(banner.id) || identifier(banner.uid),
      materialType: banner.material_type === 'video' ? 'video' : 'image',
      pcImage: text(banner.pc_image),
      wapImage: text(banner.wap_image),
      pcVideo: text(banner.pc_video),
      wapVideo: text(banner.wap_video),
      title: text(banner.title),
      titleColor: text(banner.title_color) || '#FFFFFF',
      buttonText: text(banner.button_text),
      buttonColor: text(banner.button_color) || '#FFFFFF',
      buttonTextColor: text(banner.button_text_color) || '#2B2B2B',
      buttonJump: isRecord(banner.button_jump) ? (banner.button_jump as JumpTarget) : undefined,
      alt: text(banner.alt),
    },
    series: {
      title: text(series.title),
      subTitle: text(series.sub_title),
      items: normalizeSeriesItems(series.items),
    },
    media: {
      video: {
        title: text(video.title),
        pcVideo: text(video.pc_video),
        wapVideo: text(video.wap_video),
        pcCover: text(video.pc_cover),
        wapCover: text(video.wap_cover),
        visible: isVisibleOnDevice(video.display_on, deviceType),
      },
      gallery: {
        title: text(gallery.title),
        images: normalizeGalleryImages(gallery.images),
        visible: isVisibleOnDevice(gallery.display_on, deviceType),
      },
    },
    productList: {
      title: text(productList.title),
      subTitle: text(productList.sub_title),
      pcBackgroundImage: text(productList.pc_background_image),
      wapBackgroundImage: text(productList.wap_background_image),
      productGroupId: positiveInteger(productList.product_group_id),
    },
    productRecommend: productRecommend
      ? {
          sectionTitle: text(productRecommend.section_title),
          sourceType:
            productRecommend.source_type === 'algorithm' || productRecommend.source_type === 'group'
              ? productRecommend.source_type
              : '',
          algorithmValue: text(productRecommend.algorithm_value),
          groupId: positiveInteger(productRecommend.group_id),
          visible: isVisibleOnDevice(productRecommend.display_on, deviceType),
        }
      : undefined,
  };
}
